import { ArrowLeft, CheckCircle, Columns, Home, Loader, Lock, MinusCircle } from "react-feather"
import { useNavigate } from "react-router-dom"
import { useOwnerSummary } from "@/api/generated/endpoints/api"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent, EmptyCard } from "@/components/ui/card/card"
import { Loading } from "@/components/ui/loading"

// 型定義
interface RoomUsageItem {
  propertyName: string
  roomNumber: string
  planName: string
  price: number | null
}

// 金額フォーマット
function formatPrice(price: number | null): string {
  if (price === null) return "-"
  return `¥${price.toLocaleString()}`
}

// サマリーカード内の小カード
interface StatCardProps {
  label: string
  value: number
  unit: string
  icon: React.ReactNode
  iconColor?: string
}

function StatCard({ label, value, unit, icon, iconColor = "text-slate-600" }: StatCardProps) {
  return (
    <div className="flex-1 bg-slate-50 rounded-md p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-500">{label}</span>
        <span className={iconColor}>{icon}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-slate-600">{value}</span>
        <span className="text-sm font-medium text-slate-600">{unit}</span>
      </div>
    </div>
  )
}

// ルームリストアイテム
interface RoomListItemProps {
  item: RoomUsageItem
}

function RoomListItem({ item }: RoomListItemProps) {
  return (
    <div className="flex items-center justify-between bg-slate-50 rounded px-1.5 py-2">
      <div className="flex items-center gap-1.5 text-xs text-slate-600">
        <span className="w-[100px] font-semibold truncate">{item.propertyName}</span>
        <span className="w-[60px] font-medium">{item.roomNumber}</span>
        <span className="w-[80px] font-medium">{item.planName}</span>
      </div>
      <span className="w-[60px] text-sm font-semibold text-slate-600 text-right">
        {formatPrice(item.price)}
      </span>
    </div>
  )
}

// セクションカード
interface SectionCardProps {
  title: string
  description: string
  icon: React.ReactNode
  iconColor: string
  items: RoomUsageItem[]
  emptyText: string
}

function SectionCard({ title, description, icon, iconColor, items, emptyText }: SectionCardProps) {
  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <h3 className="text-base font-semibold text-[#354267]">{title}</h3>
            <span className={iconColor}>{icon}</span>
          </div>
          <p className="text-sm font-medium text-slate-400">{description}</p>
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-600">{items.length}件</p>
          {items.length > 0 ? (
            <div className="space-y-1.5">
              {items.map((item, index) => (
                <RoomListItem
                  key={`${item.propertyName}-${item.roomNumber}-${index}`}
                  item={item}
                />
              ))}
            </div>
          ) : (
            <EmptyCard text={emptyText} className="border-none shadow-none" />
          )}
        </div>{" "}
      </CardContent>
    </Card>
  )
}

export function SubscriptionsPage() {
  const navigate = useNavigate()
  const { data: summaryData, isLoading } = useOwnerSummary()

  // 現在の年月を取得
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1
  const currentDay = now.getDate()

  // ローディング中の表示
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="space-y-5">
          <div className="flex items-center gap-2.5">
            <Button onClick={() => navigate(-1)} variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 text-primary" />
              戻る
            </Button>
            <h1 className="text-base font-semibold text-[#354267]">サービス利用状況</h1>
          </div>
          <Loading className="py-20" />
        </div>
      </div>
    )
  }

  // APIレスポンスからデータを変換
  const summary = {
    totalProperties: summaryData?.propertyCount ?? 0,
    totalRooms: summaryData?.roomCount ?? 0,
    activeCount: summaryData?.roomsOfCurrentAndContinuePlan.length ?? 0,
    startingThisMonth: summaryData?.roomsOfFuturePlan.length ?? 0,
    stoppingThisMonth: summaryData?.roomsOfCurrentAndStopPlan.length ?? 0,
    unusedCount: summaryData?.roomsOfNoPlan.length ?? 0,
    monthlyTotal: summaryData?.invoice?.totalAmount ?? 0,
  }

  const activeRooms: RoomUsageItem[] =
    summaryData?.roomsOfCurrentAndContinuePlan.map((room) => ({
      propertyName: room.property?.name ?? "-",
      roomNumber: room.name ?? "-",
      planName: room.plans?.find((p) => p.startDate && !p.endDate)?.planMaster?.name ?? "-",
      price: room.plans?.find((p) => p.startDate && !p.endDate)?.monthlyPrice ?? null,
    })) ?? []

  const stoppingRooms: RoomUsageItem[] =
    summaryData?.roomsOfCurrentAndStopPlan.map((room) => ({
      propertyName: room.property?.name ?? "-",
      roomNumber: room.name ?? "-",
      planName: room.plans?.find((p) => p.startDate && p.endDate)?.planMaster?.name ?? "-",
      price: room.plans?.find((p) => p.startDate && p.endDate)?.monthlyPrice ?? null,
    })) ?? []

  const startingRooms: RoomUsageItem[] =
    summaryData?.roomsOfFuturePlan.map((room) => ({
      propertyName: room.property?.name ?? "-",
      roomNumber: room.name ?? "-",
      planName: room.plans?.find((p) => p.startDate)?.planMaster?.name ?? "-",
      price: 0,
    })) ?? []

  const unusedRooms: RoomUsageItem[] =
    summaryData?.roomsOfNoPlan.map((room) => ({
      propertyName: room.property?.name ?? "-",
      roomNumber: room.name ?? "-",
      planName: "-",
      price: null,
    })) ?? []

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center gap-2.5">
          <Button onClick={() => navigate(-1)} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 text-primary" />
            戻る
          </Button>
          <h1 className="text-base font-semibold text-[#354267]">サービス利用状況</h1>
        </div>

        {/* 年月表示 */}
        <div className="flex items-center justify-center gap-2.5 bg-slate-100 rounded-md py-2.5 px-4">
          <span className="text-base font-semibold text-[#354267]">
            {currentYear}年{currentMonth}月利用状況
          </span>
          <span className="text-base font-semibold text-slate-400">
            {currentMonth}月{currentDay}日時点
          </span>
        </div>

        {/* サービス利用概要 */}
        <Card>
          <CardContent className="p-4 space-y-5">
            <h3 className="text-base font-semibold text-[#354267]">サービス利用概要</h3>

            <div className="space-y-2.5">
              {/* 総物件数・総部屋数 */}
              <div className="flex gap-2.5">
                <StatCard
                  label="総物件数"
                  value={summary.totalProperties}
                  unit="棟"
                  icon={<Home className="h-4 w-4" />}
                />
                <StatCard
                  label="総部屋数"
                  value={summary.totalRooms}
                  unit="戸"
                  icon={<Columns className="h-4 w-4" />}
                />
              </div>

              {/* 利用中・今月利用開始 */}
              <div className="flex gap-2.5">
                <StatCard
                  label="利用中"
                  value={summary.activeCount}
                  unit="件"
                  icon={<CheckCircle className="h-4 w-4" />}
                  iconColor="text-emerald-400"
                />
                <StatCard
                  label="今月利用開始"
                  value={summary.startingThisMonth}
                  unit="件"
                  icon={<Loader className="h-4 w-4" />}
                  iconColor="text-blue-500"
                />
              </div>

              {/* 今月利用停止・未利用 */}
              <div className="flex gap-2.5">
                <StatCard
                  label="今月利用停止"
                  value={summary.stoppingThisMonth}
                  unit="件"
                  icon={<MinusCircle className="h-4 w-4" />}
                  iconColor="text-red-500"
                />
                <StatCard
                  label="未利用"
                  value={summary.unusedCount}
                  unit="件"
                  icon={<Lock className="h-4 w-4" />}
                  iconColor="text-slate-400"
                />
              </div>
            </div>

            {/* 月額合計 */}
            <div className="flex items-center justify-between bg-slate-50 rounded-md p-4">
              <span className="text-sm font-semibold text-slate-600">月額合計</span>
              <span className="text-lg font-bold text-slate-600">
                ¥{summary.monthlyTotal.toLocaleString()}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* 利用中 */}
        <SectionCard
          title="利用中"
          description="以下の部屋はプランに加入中です"
          icon={<CheckCircle className="h-[18px] w-[18px]" />}
          iconColor="text-emerald-400"
          items={activeRooms}
          emptyText="利用中の部屋がありません"
        />

        {/* 今月利用停止 */}
        <SectionCard
          title="今月利用停止"
          description="以下は今月でプランが終了する部屋です"
          icon={<MinusCircle className="h-[18px] w-[18px]" />}
          iconColor="text-red-500"
          items={stoppingRooms}
          emptyText="停止予定の部屋がありません"
        />

        {/* 今月利用開始 */}
        <SectionCard
          title="今月利用開始"
          description="以下は初月無料対象の部屋です"
          icon={<Loader className="h-[18px] w-[18px]" />}
          iconColor="text-blue-500"
          items={startingRooms}
          emptyText="開始予定の部屋がありません"
        />

        {/* 未利用 */}
        <SectionCard
          title="未利用"
          description="以下の部屋はプランに未加入です"
          icon={<Lock className="h-[18px] w-[18px]" />}
          iconColor="text-slate-400"
          items={unusedRooms}
          emptyText="部屋がありません"
        />
      </div>
    </div>
  )
}
